import React, { useLayoutEffect, useMemo, useRef } from "react";
import Slider from "rc-slider";
import type { SliderProps } from "rc-slider";

const SLIDER_TOOLTIP_LEFT_VAR = "--reactify-slider-handle-left";

type TooltipHandleMeta = { value: number };

function SliderTooltipRcHandle({
  handleNode,
  value,
  min,
  max
}: {
  handleNode: React.ReactElement;
  value: number;
  min: number;
  max: number;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el) return;
    const safeMin = Number.isFinite(min) ? min : 0;
    const safeMax = Number.isFinite(max) ? max : 100;
    const span = safeMax - safeMin || 1;
    const offsetPct = ((value - safeMin) / span) * 100;
    el.style.setProperty(SLIDER_TOOLTIP_LEFT_VAR, `${offsetPct}%`);
  }, [value, min, max]);

  return (
    <div className="rc-slider-tooltip-wrap position-relative d-inline-block w-100">
      <div
        ref={tooltipRef}
        className="rc-slider-tooltip reactify-slider-tooltip-handle-positioned"
      >
        {value} Miles
      </div>
      {handleNode}
    </div>
  );
}

const createTooltipHandleRender =
  (min: number, max: number) =>
  (handleNode: React.ReactElement, { value }: TooltipHandleMeta) => (
    <SliderTooltipRcHandle
      handleNode={handleNode}
      value={value}
      min={min}
      max={max}
    />
  );

type SliderTooltipProps = Omit<SliderProps, "handleRender"> & {
  /** Ignored; kept for compatibility with older call sites. */
  handle?: unknown;
};

export function SliderTooltip({
  handle: _handle,
  min,
  max,
  ...sliderProps
}: SliderTooltipProps) {
  const handleRender = useMemo(
    () => createTooltipHandleRender(min ?? 0, max ?? 100),
    [min, max]
  );
  return (
    <Slider
      handleRender={handleRender}
      min={min}
      max={max}
      {...sliderProps}
    />
  );
}

export function RangeTooltip({
  handle: _handle,
  min,
  max,
  ...sliderProps
}: SliderTooltipProps) {
  const handleRender = useMemo(
    () => createTooltipHandleRender(min ?? 0, max ?? 100),
    [min, max]
  );
  return (
    <Slider
      range
      handleRender={handleRender}
      min={min}
      max={max}
      {...sliderProps}
    />
  );
}
